<template>
  <div class="dea-card-resource-container">
    <div v-if="enableLayoutSwitch" class="dea-card-resource__switcher">
      <div v-if="layout === 'compressed'">
        <Links
          :class="linkColor"
          class="dea-card-resource__switcher-switch"
          label-class="dea-heading--regular-xs "
          icon="list-list"
          @click="layout = cardLayout"
          >visualizza a lista</Links
        >
      </div>
      <div v-else>
        <Links
          :class="linkColor"
          class="dea-card-resource__switcher-switch"
          label-class="dea-heading--regular-xs "
          icon="list-grid"
          @click="layout = 'compressed'"
          >visualizza a griglia</Links
        >
      </div>
    </div>
    <CardResource
      v-for="item in items"
      :key="item.resource_uuid"
      :bookmark-enabled="bookmarkEnabled"
      :bookmark="bookmarks[item.resource_uuid]"
      :class="cardExtraClasses"
      :color-type-modifier="colorTypeModifier"
      :resource="item"
      :share-enabled="shareEnabled"
      :card-layout="layout"
      :tag1="tag1"
      :tag2="tag2"
      :chips="chips"
      :check-active-chip="checkActiveChip"
      :tag4="tag4"
      :highlight-text="highlightText"
      :show-type-icon="showTypeIcon"
      :show-image="showImage"
      :show-teacher-label="showTeacherLabel"
      :accent-modifier="accentModifier"
      :action-accent-modifier="actionAccentModifier"
      :use-share-link="useShareLink"
      :show-bookmark-origin="showBookmarkOrigin"
      :force-opening="openResourceMethod"
      :use-accordion="hasAccordion(item, accordionComponent)"
      @resource-locked="openModal"
      @resource-opened="$emit('resource-opened', $event)"
      @save-bookmark="saveBookmark"
      @delete-bookmark="deleteBookmark"
      @dropdown-toggled="$emit('dropdown-toggled', $event)"
      @accordion-expanded="$emit('accordion-expanded', $event)"
    >
      <template slot="accordion-content">
        <component :is="accordionComponent" :resource="item"></component>
      </template>
    </CardResource>
    <template v-if="fillerItems">
      <CardResource
        v-for="(item, key) in new Array(fillerItems)"
        :key="`resource-filler-${key}`"
        :resource="placeholderResource"
        :card-layout="layout"
        class="dea-card-resource--filler"
      />
    </template>
    <ResourcesModal v-if="locked" :user="user" :item="locked" @close-modal="closeModal" />
  </div>
</template>

<script>
import bookmarksSetup from "@/components/mixins/bookmarksSetup";
import handleWindowResize from "@/components/mixins/handleWindowResize";
import variables from "@/styles/variables.scss";
import resourceList from "@/components/mixins/resourceList";
import Links from "@/components/elements/Links";
import CardResource from "@/components/patterns/CardResource";

export default {
  name: "ResourcesList",
  components: { CardResource, Links },
  mixins: [handleWindowResize, resourceList, bookmarksSetup],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    currentGrade: {
      type: String,
      default: "",
    },
    cardLayout: {
      type: String,
      default: "expanded",
    },
    enableLayoutSwitch: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      required: true,
    },
    tag1: {
      type: [String, Function],
      default: "",
    },
    tag2: {
      type: [String, Function],
      default: "",
    },
    chips: {
      type: [Function, Array],
      default: () => [],
    },
    tag4: {
      type: [Array, String, Function],
      default: () => [],
    },
    highlightText: {
      type: [String, Function],
      default: "",
    },
    showImage: {
      type: Boolean,
      default: true,
    },
    showTeacherLabel: {
      type: Boolean,
      default: true,
    },
    showTypeIcon: {
      type: [Boolean, Function],
      default: true,
    },
    useShareLink: {
      type: Boolean,
      default: false,
    },
    showBookmarkOrigin: {
      type: Boolean,
      default: false,
    },
    cardExtraClasses: {
      type: String,
      default: "",
    },
    accentModifier: {
      type: String,
      default: "docenti",
    },
    actionAccentModifier: {
      type: String,
      default: "default",
    },
    columns: {
      type: Number,
      default: 4,
    },
    checkActiveChip: {
      type: Function,
      default: () => ({}),
    },
    accordionComponent: {
      type: Object,
      default: null,
    },
    /**
     * Force opening method according to the given value (if a function, the function will be called instead of any opening method declared on the resource)
     */
    openResourceMethod: {
      type: [String, Function],
      default: null,
    },
    useFillerItems: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // removed $root.config for the 3 props below because replaced by a prop
      colorTypeModifier: this.config ? this.config.card_color_type_modifier : null,
      shareEnabled: this.config ? this.config.share_resource_enabled : null,
      // used for filler items
      placeholderResource: {
        assets: [],
      },
      cardsColumns: this.columns,
      // number of empty CardResource to be added to fill the grid and have an even numbers of cards
      fillerItems: this.getFillerItems(),
      layout: this.cardLayout,
      bookmarksSave: "saveResource",
      bookmarksGet: "getResources",
      bookmarkType: "resource",
    };
  },
  computed: {
    bookmarkEnabled() {
      return Boolean(this.config.bookmarks_resource_enabled && this.user.authenticated);
    },
    linkColor() {
      if (this.accentModifier === "docenti") return "dea-links--target-color-blue-docenti";
      if (this.accentModifier === "famiglie") return "dea-links--target-color-green-famiglie";
      if (this.accentModifier === "studenti") return "dea-links--target-color-yellow-studenti";
      return "dea-links--generic-dark-grey";
    },
  },
  watch: {
    items() {
      this.fillerItems = this.getFillerItems();
    },
  },
  mounted() {
    this.$emit("resources-list-mounted");
  },
  methods: {
    hasAccordion(resource, accordionComponent) {
      if (accordionComponent) {
        return accordionComponent.methods.hasAccordion(resource);
      }
      return false;
    },
    getFillerItems() {
      if (!this.useFillerItems) return 0;
      if (this.cardLayout === "compressed") return this.items.length % this.cardsColumns;
      return this.columns - (this.items.length % this.columns);
    },
    handleWindowResize(event) {
      this.windowWidth = this.getWindowWidth(event);
      if (this.windowWidth < variables.bpSizeSmall) {
        this.cardsColumns = 1;
      } else if (this.windowWidth < variables.bpSizeMedium) {
        this.cardsColumns = this.columns > 2 ? this.columns - 2 : 1;
      } else if (this.windowWidth < variables.bpSizeLarge) {
        this.cardsColumns = this.columns - 1;
      } else {
        this.cardsColumns = this.columns;
      }
      this.fillerItems = this.getFillerItems();
    },
  },
};
</script>
<style lang="scss">
@import "@/styles/system.utils";

.dea-links__modal > .dea-links__label {
  font-size: $h-font-size-x-large;
}

.dea-card-resource__switcher {
  display: flex;
  justify-content: flex-end;
  margin: $space-small 0 $space-base;
  width: 100%;

  &-switch {
    cursor: pointer;

    .dea-links__icon {
      margin-left: $space-x-small;

      &.dea-ico-list-list,
      &.dea-ico-list-grid {
        height: $space-base;
        width: $space-base;
      }

      &.dea-ico-list-list {
        background-image: url("data:image/svg+xml,%3Csvg id='Layer_1' data-name='Layer 1' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M29.18,9H7.82A.93.93,0,0,1,7,8a.93.93,0,0,1,.82-1H29.18A.93.93,0,0,1,30,8,.93.93,0,0,1,29.18,9ZM30,16a.93.93,0,0,0-.82-1H7.82a1,1,0,0,0,0,2H29.18A.93.93,0,0,0,30,16Zm0,8a.93.93,0,0,0-.82-1H7.82a1,1,0,0,0,0,2H29.18A.93.93,0,0,0,30,24Z'/%3E%3Ccircle cx='3' cy='8' r='1'/%3E%3Ccircle cx='3' cy='16' r='1'/%3E%3Ccircle cx='3' cy='24' r='1'/%3E%3C/svg%3E");
      }

      &.dea-ico-list-grid {
        background-image: url("data:image/svg+xml,%3Csvg id='Layer_1' data-name='Layer 1' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M14.13,4H4.89A.89.89,0,0,0,4,4.89v9.24a.89.89,0,0,0,.89.89h9.24a.89.89,0,0,0,.89-.89V4.89A.89.89,0,0,0,14.13,4Zm-.89,9.24H5.78V5.78h7.46Z'/%3E%3Cpath d='M27.11,4H17.87a.89.89,0,0,0-.89.89v9.24a.89.89,0,0,0,.89.89h9.24a.89.89,0,0,0,.89-.89V4.89A.89.89,0,0,0,27.11,4Zm-.89,9.24H18.76V5.78h7.46Z'/%3E%3Cpath d='M14.13,17H4.89a.89.89,0,0,0-.89.89v9.24a.89.89,0,0,0,.89.89h9.24a.89.89,0,0,0,.89-.89V17.87A.89.89,0,0,0,14.13,17Zm-.89,9.24H5.78V18.76h7.46Z'/%3E%3Cpath d='M27.11,17H17.87a.89.89,0,0,0-.89.89v9.24a.89.89,0,0,0,.89.89h9.24a.89.89,0,0,0,.89-.89V17.87A.89.89,0,0,0,27.11,17Zm-.89,9.24H18.76V18.76h7.46Z'/%3E%3C/svg%3E");
      }
    }
  }
}
</style>
